// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface UseUnsavedChangesPromptOptions {
  hasUnsavedChanges: boolean;
  message?: string;
}

/**
 * Hook to prompt user when navigating away from a page with unsaved changes
 * Shows browser's native prompt when trying to leave the page or close the browser
 * @param options - Configuration options
 * @param options.hasUnsavedChanges - Boolean indicating if there are unsaved changes
 * @param options.message - Optional custom message to display in the prompt (note: modern browsers ignore custom messages)
 */
export const useUnsavedChangesPrompt = ({ hasUnsavedChanges, message }: UseUnsavedChangesPromptOptions) => {
  const { t } = useTranslation();

  const defaultMessage =
    message || t('portal.unsaved-changes.message', 'You have unsaved changes. Do you want to continue?');

  // Handle browser refresh/close and navigation
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore the custom message and show their own
      e.returnValue = defaultMessage;
      return defaultMessage;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, defaultMessage]);
};

